import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { getAnticipos, createAnticipo, updateAnticipo } from '../api'; // Import API functions
import { Modal as Miuimodal, Box, Typography, TextField, Button, IconButton, Autocomplete, FormControl, Stack, InputLabel, OutlinedInput } from '@mui/material';
import { Add, Delete, Edit, Print, Search, FilterAlt } from '@mui/icons-material';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid'; // Importar uuid para generar identificadores únicos
import AnticipoPDF from '../components/AnticipoPDF'; // Importar el nuevo componente
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import CircularProgress from '@mui/material/CircularProgress';

const Anticipo = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('BI-Dashboard');
  const [anticipos, setAnticipos] = useState([]);
  const [filteredAnticipos, setFilteredAnticipos] = useState([]);
  const [newAnticipo, setNewAnticipo] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [warehouseData, setWarehouseData] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]);
  const [searchReference, setSearchReference] = useState('');
  const [searchedShipment, setSearchedShipment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // Add this state to track edit mode
  const [searchValue, setSearchValue] = useState(''); // Added for Topbar search
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Filter states
  const [filters, setFilters] = useState({
    folio: '',
    reference: '',
    consignatario: '',
    creationDateRange: [null, null]
  });
  
  const [showFilters, setShowFilters] = useState(false);

  const expenseConcepts = [
    'viaticos', 'casetas', 'diesel', 'hospedaje', 'maniobras', 'transbordos', 'ferry', 'alimentos', 'combustible', 'deposito', 'permisos', 'otros_gastos', 'pensiones', 'talachas'
  ];

  useEffect(() => {
    // Ahora solo se necesita una llamada para obtener anticipos con shipment_data
    const fetchAnticipos = async () => {
      setLoading(true);
      try {
        const anticiposData = await getAnticipos();
        // Ordenar anticipos por fecha de creación
        const sortedAnticipos = [...anticiposData].sort((a, b) => {
          const dateA = new Date(a.created_dt || 0);
          const dateB = new Date(b.created_dt || 0);
          return dateB - dateA;
        });
        setAnticipos(sortedAnticipos);
      } catch (error) {
        console.error('Error fetching anticipos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnticipos();
    fetchWarehouseData();
    const params = new URLSearchParams(location.search);
    const shipmentReference = params.get('shipment_reference');
    if (shipmentReference) {
      setNewAnticipo({ shipment: shipmentReference });
      handleSearchShipment(shipmentReference);
      setModalOpen(true);
    }
  }, [location.search]);

  // Set filtered anticipos when anticipos are fetched
  useEffect(() => {
    setFilteredAnticipos(anticipos);
  }, [anticipos]);

  // Function to apply filters to the anticipos list
  const applyFilters = () => {
    const filtered = anticipos.filter(anticipo => {
      const shipment = anticipo.shipment_data || {};
      
      // Filter by folio
      const matchesFolio = filters.folio ? 
        anticipo.folio.toLowerCase().includes(filters.folio.toLowerCase()) : true;
      
      // Filter by shipment reference
      const matchesReference = filters.reference ? 
        anticipo.shipment?.toLowerCase().includes(filters.reference.toLowerCase()) : true;
      
      // Filter by consignatario (client/customer)
      const matchesConsignatario = filters.consignatario ?
        shipment.customer?.toLowerCase().includes(filters.consignatario.toLowerCase()) : true;
      
      // Filter by creation date range using created_dt field
      let matchesDateRange = true;
      if (filters.creationDateRange[0] && filters.creationDateRange[1]) {
        // Use created_dt field as specified
        const creationDate = new Date(anticipo.created_dt);
        const startDate = new Date(filters.creationDateRange[0]);
        const endDate = new Date(filters.creationDateRange[1]);
        endDate.setHours(23, 59, 59, 999); // Set end date to end of day
        
        matchesDateRange = creationDate >= startDate && creationDate <= endDate;
      }
      
      return matchesFolio && matchesReference && matchesConsignatario && matchesDateRange;
    });
    
    setFilteredAnticipos(filtered);
  };

  const fetchWarehouseData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/bodegas`);
      setWarehouseData(response.data);
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    }
  };

  const generateUniqueFolio = () => {
    return `ANT-${uuidv4().slice(0, 6)}`;
  };

  const handleCreateAnticipo = async () => {
    try {
      const shipment = newAnticipo.shipment_data || {};
      const anticipoData = {
        ...newAnticipo,
        folio: generateUniqueFolio(),
        origen: shipment.origin || 'No especificado',
        destino: getDestinationCity(shipment) || 'No especificado',
        ...expenseItems.reduce((acc, item) => {
          acc[item.concept] = parseFloat(item.amount) || 0;
          return acc;
        }, {})
      };
      const createdAnticipo = await createAnticipo(anticipoData);
      setAnticipos([...anticipos, createdAnticipo]);
      setNewAnticipo({});
      setExpenseItems([]);
      setModalOpen(false);
    } catch (error) {
      console.error('Error creating anticipo:', error);
    }
  };

  const handleUpdateAnticipo = async () => {
    try {
      if (!newAnticipo || newAnticipo.id === undefined) {
        alert(`Error: No se puede actualizar el anticipo porque el ID no está definido.`);
        return;
      }
      const shipment = newAnticipo.shipment_data || {};
      const updatedAnticipoData = {
        ...newAnticipo,
        origen: shipment.origin || newAnticipo.origen || 'No especificado',
        destino: getDestinationCity(shipment) || newAnticipo.destino || 'No especificado',
        ...expenseItems.reduce((acc, item) => {
          acc[item.concept] = parseFloat(item.amount) || 0;
          return acc;
        }, {})
      };
      const idToUse = parseInt(newAnticipo.id, 10);
      if (isNaN(idToUse)) {
        alert(`Error: El ID del anticipo no es válido.`);
        return;
      }
      const updatedAnticipo = await updateAnticipo(idToUse, updatedAnticipoData);
      setAnticipos(anticipos.map(a => (a.id === idToUse ? updatedAnticipo : a)));
      setNewAnticipo({});
      setExpenseItems([]);
      setIsEditMode(false);
      setModalOpen(false);
    } catch (error) {
      console.error('Error updating anticipo:', error);
      alert(`Error al actualizar el anticipo: ${error.message}`);
    }
  };

  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.clear();
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const getDestinationCity = (shipment) => {
    if (!shipment || !shipment.orders || shipment.orders.length === 0) return 'N/A';
    const order = shipment.orders[0];
    if (!order.D_R) return order.City || 'N/A';
    const warehouse = warehouseData.find(w => w.nombre === order.customer || w.nombre === order.Customer);
    return warehouse ? warehouse.ciudad_municipio : order.City || 'N/A';
  };

  const calculateTotalAnticipo = (anticipo) => {
    const fields = ['viaticos', 'casetas', 'diesel', 'hospedaje', 'maniobras', 'transbordos', 'ferry', 'alimentos', 'combustible', 'deposito', 'permisos', 'otros_gastos', 'pensiones', 'talachas'];
    return fields.reduce((total, field) => total + (parseFloat(anticipo[field]) || 0), 0);
  };

  const handleAddExpenseItem = () => {
    setExpenseItems([...expenseItems, { concept: '', amount: '' }]);
  };

  const handleRemoveExpenseItem = (index) => {
    // If we're in edit mode and removing an existing concept, add it to a list of concepts to be nullified
    if (isEditMode && expenseItems[index] && expenseItems[index].concept) {
      // Get the concept name that's being removed
      const conceptToNullify = expenseItems[index].concept;
      
      // Update the newAnticipo object by explicitly setting this concept to null
      // This ensures it will be sent as null to the database when updating
      setNewAnticipo(prev => ({
        ...prev,
        [conceptToNullify]: null
      }));
    }
    
    // Remove the item from the expenseItems array as before
    setExpenseItems(expenseItems.filter((_, i) => i !== index));
  };

  const handleExpenseItemChange = (index, field, value) => {
    const updatedItems = [...expenseItems];
    updatedItems[index][field] = value;
    setExpenseItems(updatedItems);
  };

  // Updated to ensure we preserve the anticipo data correctly
  const handleEditAnticipo = (anticipo) => {
    // Make sure the ID is correctly passed and preserved
    if (!anticipo || anticipo.id === undefined) {
      alert("Error: El ID del anticipo no está disponible.");
      return;
    }

    setIsEditMode(true);
    
    // Directly set all the anticipo data including ID
    setNewAnticipo({...anticipo});
    
    setExpenseItems(expenseConcepts
      .filter(concept => anticipo[concept] && anticipo[concept] > 0)
      .map(concept => ({
        concept,
        amount: anticipo[concept] || ''
      }))
    );
    
    // First set the modal open to show the loading state
    setModalOpen(true);
    
    // Then fetch shipment details but preserve all anticipo data
    fetchShipmentForEdit(anticipo.shipment, anticipo);
  };

  // Special function to fetch shipment while preserving the anticipo data
  const fetchShipmentForEdit = async (reference, originalAnticipo) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/shipments?reference=${reference}`);
      setSearchedShipment(response.data[0]);
      
      // Re-set the anticipo with its ID to ensure it's not lost
      setNewAnticipo({...originalAnticipo});
      
    } catch (error) {
      console.error('Error searching shipment:', error);
      setSearchedShipment(null);
    }
  };

  // Regular search function for new anticipos
  const handleSearchShipment = async (reference) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/shipments?reference=${reference || searchReference}`);
      setSearchedShipment(response.data[0]);
      
      // Only update shipment reference for new anticipos
      if (!isEditMode) {
        setNewAnticipo({ shipment: reference || searchReference });
      }
    } catch (error) {
      console.error('Error searching shipment:', error);
      setSearchedShipment(null);
    }
  };

  const handlePrintAnticipo = (anticipo) => {
    // Implementar la lógica de impresión aquí
    console.log('Imprimir anticipo:', anticipo);
  };

  const handleOpenNewAnticipoModal = () => {
    setIsEditMode(false);
    setNewAnticipo({});
    setExpenseItems([]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setNewAnticipo({});
    setExpenseItems([]);
    setSearchedShipment(null);
    setSearchReference('');
    setIsEditMode(false);
    setModalOpen(false);
  };

  // Función para descargar anticipos filtrados en XLSX
  const handleDownloadXLSX = () => {
    const dataToExport = filteredAnticipos.map(anticipo => {
      const shipment = anticipo.shipment_data || {};
      const conceptos = {};
      expenseConcepts.forEach(concept => {
        conceptos[concept.charAt(0).toUpperCase() + concept.slice(1)] = anticipo[concept] !== undefined ? anticipo[concept] : 0;
      });
      return {
        'Fecha de creación': anticipo.created_dt ? new Date(anticipo.created_dt).toLocaleDateString() : '',
        'Folio': anticipo.folio,
        'Referencia': shipment.reference || '',
        'Consignatario': shipment.customer || '',
        'Origen': shipment.origin || '',
        'Destino': getDestinationCity(shipment),
        'Conductor': shipment.driver || '',
        ...conceptos,
        'Total Anticipo': calculateTotalAnticipo(anticipo),
        'Comentarios': anticipo.comentarios || ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Anticipos');
    XLSX.writeFile(wb, 'anticipos_filtrados.xlsx');
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />
      <div className={`transition-all flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar 
          showSearch={true} 
          searchValue={searchValue} 
          onSearchChange={(value) => {
            setSearchValue(value);
            setFilters(prev => ({ ...prev, reference: value }));
          }}
        />
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <CircularProgress size={60} color="primary" />
          </div>
        ) : (
          <>
            <div className="mt-6 mb-4 flex justify-between items-center">
              <Button 
                className="flex items-center" 
                onClick={handleOpenNewAnticipoModal}
                variant="contained" 
                color="primary"
              >
                <Add className="w-6 h-6 mr-2" />
                Nuevo Anticipo
              </Button>
              <Button
                className="flex items-center ml-2"
                onClick={handleDownloadXLSX}
                variant="outlined"
                color="success"
              >
                Descargar XLSX
              </Button>
            </div>
            
            <div>
              {/* Filter Bar */}
              <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Filtros</h3>
                  <Button
                    onClick={() => setShowFilters(!showFilters)}
                    startIcon={<FilterAlt />}
                    variant="outlined"
                    size="small"
                  >
                    {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
                  </Button>
                </div>
                
                {showFilters && (
                  <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <FormControl sx={{ minWidth: 180 }} size="small">
                      <TextField
                        label="Folio"
                        name="folio"
                        value={filters.folio}
                        onChange={(e) => setFilters({...filters, folio: e.target.value})}
                        size="small"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </FormControl>

                    <FormControl sx={{ minWidth: 180 }} size="small">
                      <TextField
                        label="Referencia"
                        name="reference"
                        value={filters.reference}
                        onChange={(e) => setFilters({...filters, reference: e.target.value})}
                        size="small"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </FormControl>

                    <FormControl sx={{ minWidth: 180 }} size="small">
                      <TextField
                        label="Consignatario"
                        name="consignatario"
                        value={filters.consignatario}
                        onChange={(e) => setFilters({...filters, consignatario: e.target.value})}
                        size="small"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </FormControl>
                    
                    <div className="filter-container" style={{ minWidth: 180 }}>
                      <div className="react-datepicker-wrapper">
                        <DatePicker
                          selected={filters.creationDateRange[0]}
                          onChange={(dates) => {
                            const [start, end] = dates;
                            setFilters(prev => ({
                              ...prev,
                              creationDateRange: [start, end]
                            }));
                          }}
                          startDate={filters.creationDateRange[0]}
                          endDate={filters.creationDateRange[1]}
                          selectsRange
                          placeholderText="Rango de fecha de creación"
                          className="p-2 border rounded-md w-full bg-white"
                          dateFormat="dd/MM/yyyy"
                        />
                      </div>
                    </div>

                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => applyFilters()}
                      sx={{ height: 40 }}
                    >
                      Aplicar
                    </Button>
                    
                    <Button 
                      variant="outlined"
                      onClick={() => {
                        setFilters({
                          folio: '',
                          reference: '',
                          consignatario: '',
                          creationDateRange: [null, null]
                        });
                        setFilteredAnticipos(anticipos);
                      }}
                      sx={{ height: 40 }}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                )}
              </div>
              
              {/* Render anticipos and form to create new anticipo */}
              <div className="mb-6">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr>
                      <th className='py-2 px-4 border-b'>Fecha de creación</th>
                      <th className="py-2 px-4 border-b">Folio</th>
                      <th className="py-2 px-4 border-b">Referencia</th>
                      <th className="py-2 px-4 border-b">Consignatario</th>
                      <th className="py-2 px-4 border-b">Origen</th>
                      <th className="py-2 px-4 border-b">Destino</th>
                      <th className="py-2 px-4 border-b">Total Anticipo</th>
                      <th className="py-2 px-4 border-b">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnticipos.map(anticipo => {
                      const shipment = anticipo.shipment_data || {};
                      return (
                        <tr key={anticipo.id}>
                          <td className="py-2 px-4 border-b">{new Date(anticipo.created_dt).toLocaleDateString()}</td>
                          <td className="py-2 px-4 border-b">{anticipo.folio}</td>
                          <td className="py-2 px-4 border-b">{shipment.reference || 'N/A'}</td>
                          <td className="py-2 px-4 border-b">{shipment.customer || 'N/A'}</td>
                          <td className="py-2 px-4 border-b">{shipment.origin || 'N/A'}</td>
                          <td className="py-2 px-4 border-b">{getDestinationCity(shipment)}</td>
                          <td className="py-2 px-4 border-b">${calculateTotalAnticipo(anticipo)}</td>
                          <td className="py-2 px-4 border-b">
                            <IconButton color="primary" onClick={() => handleEditAnticipo(anticipo)}>
                              <Edit />
                            </IconButton>
                            <AnticipoPDF anticipo={anticipo} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Miuimodal
                open={isModalOpen}
                onClose={handleCloseModal}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 600,
                    bgcolor: 'background.paper',
                    borderRadius: '10px',
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                  }}
                >
                  <Typography variant="h6" component="h2">
                    {isEditMode ? 'Editar Anticipo' : 'Crear Nuevo Anticipo'}
                  </Typography>
                  {!searchedShipment && !isEditMode && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Referencia del Embarque"
                        value={searchReference}
                        onChange={e => setSearchReference(e.target.value)}
                        fullWidth
                        margin="normal"
                      />
                      <Button
                        startIcon={<Search />}
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => handleSearchShipment()}
                      >
                        Buscar Embarque
                      </Button>
                    </Box>
                  )}
                  {searchedShipment && (
                    <>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1"><strong>Referencia:</strong> {searchedShipment.reference}</Typography>
                        <Typography variant="body1"><strong>Consignatario:</strong> {searchedShipment.customer}</Typography>
                        <Typography variant="body1"><strong>Origen:</strong> {searchedShipment.origin}</Typography>
                        <Typography variant="body1"><strong>Destino:</strong> {getDestinationCity(searchedShipment)}</Typography>
                      </Box>
                      
                      <TextField
                        label="Folio"
                        value={isEditMode ? newAnticipo.folio : (newAnticipo.folio || generateUniqueFolio())}
                        disabled
                        fullWidth
                        margin="normal"
                      />
                      <TextField
                        label="Comentarios/Observaciones"
                        value={newAnticipo.comentarios || ''}
                        onChange={e => setNewAnticipo({ ...newAnticipo, comentarios: e.target.value })}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={2}
                        placeholder="Ingrese aquí cualquier observación o comentario relevante"
                      />
                      {expenseItems.map((item, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={2} mt={2}>
                          <Autocomplete
                            fullWidth
                            options={expenseConcepts}
                            getOptionLabel={(option) => option}
                            value={item.concept}
                            onChange={(event, value) => handleExpenseItemChange(index, 'concept', value)}
                            renderInput={(params) => <TextField {...params} label="Concepto" required />}
                          />
                          <TextField
                            label="Monto"
                            type="number"
                            value={item.amount}
                            onChange={e => handleExpenseItemChange(index, 'amount', e.target.value)}
                            fullWidth
                            required
                          />
                          <IconButton color="error" onClick={() => handleRemoveExpenseItem(index)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<Add />}
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={handleAddExpenseItem}
                      >
                        Añadir Concepto
                      </Button>
                      <Box display="flex" justifyContent="space-between" mt={3}>
                        <Button variant="contained" color="error" onClick={handleCloseModal}>
                          Cancelar
                        </Button>
                        <Button 
                          variant="contained" 
                          color="success" 
                          onClick={isEditMode 
                            ? handleUpdateAnticipo 
                            : handleCreateAnticipo
                          }
                        >
                          {isEditMode ? 'Actualizar' : 'Guardar'}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Miuimodal>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Anticipo;
